/**
 * Честная высота фрейма для родителя (gamelib::<lessonId>::resize::<N>).
 *
 * offsetHeight контейнера `.game` не годится: при overflow:hidden и
 * min-height:0 у вложенного тела слайда контент может визуально
 * переполнять контейнер, а offsetHeight этого не покажет — родитель
 * получит заниженную высоту и обрежет предпросмотр.
 *
 * Вместо этого берём максимум из двух независимых измерений:
 *  - нижние границы всех элементов body (см. computeMaxBottom) —
 *    ловит контент, который вышел за пределы `.game`;
 *  - document.documentElement.scrollHeight — ловит случаи, когда
 *    сам body/html растянулся, а конкретные элементы обмерять не успели;
 *    учитывается только при переполнении вьюпорта (см. resolveFrameHeight).
 */

/** Минимальный интерфейс DOMRect, который нужен для расчёта — чтобы не тащить в тест реальный DOM */
export interface BottomRect {
  bottom: number;
}

/**
 * Максимум нижних границ элементов с поправкой на текущий scrollY.
 * Пустой список — 0 (вызывающая сторона сама решает, на что откатиться).
 */
export function computeMaxBottom(rects: BottomRect[], scrollY: number): number {
  if (!rects || !rects.length) return 0;
  return Math.ceil(Math.max(...rects.map((rect) => rect.bottom + scrollY)));
}

/**
 * Итоговая высота фрейма — измеренная высота контента, поднятая до scrollHeight документа
 * только при реальном переполнении вьюпорта.
 *
 * У body стоит min-height:100vh, а внутри iframe 100vh — это текущая высота самого фрейма.
 * Поэтому scrollHeight никогда не меньше viewportHeight и при scrollHeight <= viewportHeight
 * отражает лишь размер фрейма, а не контента. Если брать его как источник, после роста фрейма
 * под длинный слайд каждое следующее измерение вернёт ту же высоту (петля вверх),
 * и на короткий слайд окно не сожмётся. Строго больше вьюпорта — контент реально
 * переполняет окно (см. 4b609f8), тогда scrollHeight учитываем.
 */
export function resolveFrameHeight(
  contentHeight: number,
  documentScrollHeight: number,
  viewportHeight: number
): number {
  const content = contentHeight || 0;
  const scrollHeight = documentScrollHeight || 0;
  const overflows = scrollHeight > (viewportHeight || 0);
  return overflows ? Math.max(content, scrollHeight) : content;
}
